/**
 * Campaign Alerts Panel Component
 * Displays supplier product availability alerts for Hospital and Admin dashboards
 * Includes Request Quote functionality, delete, and live updates
 */
import { useState, useEffect, useCallback } from "react";
import { 
  Bell, 
  Package, 
  AlertTriangle, 
  Tag, 
  X, 
  Check,
  ExternalLink,
  Mail,
  Clock,
  DollarSign,
  Building2,
  Megaphone,
  Send,
  ShoppingCart,
  FileText,
  Loader2,
  Upload,
  Paperclip,
  Trash2,
  ChevronDown,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Polling interval for live updates (30 seconds)
const POLL_INTERVAL = 30000;

export default function CampaignAlertsPanel({ 
  token, 
  userType = "hospital", // "hospital" or "admin"
  onAlertClick,
  maxAlerts = 10,
  showHeader = true,
  compact = false,
  onQuoteRequested,
  enableLiveUpdates = true
}) {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Quote Request Modal State
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    quantity: "",
    notes: "",
    urgency: "normal"
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAlerts = useCallback(async (silent = false) => {
    if (!token) return;
    
    if (!silent) setIsRefreshing(true);
    
    try {
      const endpoint = userType === "admin" 
        ? "/api/campaign-alerts/admin" 
        : "/api/campaign-alerts/hospital";
      
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const newAlerts = data.alerts || [];
        
        // Check if there are new alerts (for notification)
        if (silent && newAlerts.length > alerts.length) {
          const newCount = newAlerts.length - alerts.length;
          toast.info(`${newCount} new product alert${newCount > 1 ? 's' : ''} received!`, {
            duration: 5000
          });
        }
        
        setAlerts(newAlerts);
        setUnreadCount(data.unread_count || 0);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch campaign alerts:", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [token, userType, alerts.length]);

  // Initial fetch
  useEffect(() => {
    fetchAlerts();
  }, [token]);

  // Live updates polling
  useEffect(() => {
    if (!enableLiveUpdates || !token) return;
    
    const interval = setInterval(() => {
      fetchAlerts(true); // Silent refresh
    }, POLL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [enableLiveUpdates, token, fetchAlerts]);

  const markAsRead = async (alertId) => {
    try {
      await fetch(`${API}/api/campaign-alerts/${alertId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark alert as read:", err);
    }
  };

  const handleDeleteClick = (e, alert) => {
    e.stopPropagation();
    setAlertToDelete(alert);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!alertToDelete) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/campaign-alerts/${alertToDelete.id}/delete`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertToDelete.id));
        toast.success("Alert deleted successfully");
        setShowDeleteModal(false);
        setAlertToDelete(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete alert");
      }
    } catch (err) {
      toast.error("Failed to delete alert");
    } finally {
      setDeleting(false);
    }
  };

  const handleAlertClick = (alert) => {
    if (!alert.read) {
      markAsRead(alert.id);
    }
    // Open quote request modal
    setSelectedAlert(alert);
    setQuoteForm({
      quantity: Math.min(100, alert.quantity_available || 100).toString(),
      notes: "",
      urgency: "normal"
    });
    setShowQuoteModal(true);
  };

  const handleRequestQuote = async () => {
    if (!selectedAlert || !quoteForm.quantity) {
      toast.error("Please enter the quantity you need");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/campaign-alerts/${selectedAlert.campaign_id}/request-quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity_requested: parseInt(quoteForm.quantity),
          notes: quoteForm.notes,
          urgency: quoteForm.urgency
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Quote request sent to supplier!");
        setShowQuoteModal(false);
        setSelectedAlert(null);
        
        // Update alert to show quote requested
        setAlerts(prev => prev.map(a => 
          a.id === selectedAlert.id ? { ...a, quote_requested: true } : a
        ));
        
        if (onQuoteRequested) {
          onQuoteRequested(data);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send quote request");
      }
    } catch (err) {
      toast.error("Failed to send quote request");
    } finally {
      setSubmitting(false);
    }
  };

  const getAlertIcon = (alertType) => {
    if (alertType === "backorder_mitigation") {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
    return <Tag className="w-5 h-5 text-emerald-500" />;
  };

  const getAlertBadge = (alertType) => {
    if (alertType === "backorder_mitigation") {
      return (
        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          Backorder Alert
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
        Special Offer
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
          <div className="h-16 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (alerts.length === 0 && !showHeader) {
    return null;
  }

  // Show only maxAlerts (default 10) unless showAll is true
  const displayAlerts = showAll ? alerts : alerts.slice(0, maxAlerts);
  const hiddenCount = alerts.length - maxAlerts;

  return (
    <>
      <div 
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        data-testid="campaign-alerts-panel"
      >
        {showHeader && (
          <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-slate-800">Product Alerts</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-amber-500 text-white rounded-full text-xs font-bold">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Manual Refresh Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchAlerts()}
                disabled={isRefreshing}
                className="text-slate-500 hover:text-amber-600"
                title="Refresh alerts"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              {lastUpdated && (
                <span className="text-xs text-slate-400">
                  Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        )}

        {alerts.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No product alerts at this time</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {displayAlerts.map(alert => (
                <div 
                  key={alert.id}
                  className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                    !alert.read ? "bg-amber-50/50" : ""
                  }`}
                  onClick={() => handleAlertClick(alert)}
                  data-testid={`campaign-alert-${alert.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getAlertIcon(alert.alert_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getAlertBadge(alert.alert_type)}
                        {!alert.read && (
                          <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        )}
                        {alert.quote_requested && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            Quote Requested
                          </span>
                        )}
                      </div>
                      
                      <h4 className="font-medium text-slate-900 truncate">
                        {alert.product_name}
                      </h4>
                      
                      {!compact && (
                        <>
                          <p className="text-sm text-slate-600 mt-1">
                            {alert.manufacturer && `${alert.manufacturer} • `}
                            MFR#: {alert.manufacturer_number || "N/A"}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                            <div className="flex items-center gap-1 text-emerald-600 font-medium">
                              <Package className="w-4 h-4" />
                              {alert.quantity_available} units
                            </div>
                            <div className="flex items-center gap-1 text-slate-700 font-medium">
                              <DollarSign className="w-4 h-4" />
                              ${alert.unit_price?.toFixed(2)}/unit
                            </div>
                            {alert.lead_time_days && (
                              <div className="flex items-center gap-1 text-slate-500">
                                <Clock className="w-4 h-4" />
                                {alert.lead_time_days} days
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                            <Building2 className="w-4 h-4" />
                            From: {alert.supplier_name}
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAlertClick(alert);
                        }}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Request Quote
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteClick(e, alert)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        title="Delete alert"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Show More Button */}
            {hiddenCount > 0 && !showAll && (
              <div className="p-3 bg-slate-50 border-t border-slate-100">
                <Button
                  variant="ghost"
                  className="w-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  onClick={() => setShowAll(true)}
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Show {hiddenCount} more alert{hiddenCount > 1 ? 's' : ''}
                </Button>
              </div>
            )}
            
            {/* Show Less Button */}
            {showAll && alerts.length > maxAlerts && (
              <div className="p-3 bg-slate-50 border-t border-slate-100">
                <Button
                  variant="ghost"
                  className="w-full text-slate-600 hover:text-slate-700"
                  onClick={() => setShowAll(false)}
                >
                  Show less
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Alert
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this product alert? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {alertToDelete && (
            <div className="bg-slate-50 rounded-lg p-3 my-2">
              <h4 className="font-medium text-sm text-slate-900">{alertToDelete.product_name}</h4>
              <p className="text-xs text-slate-600 mt-1">
                {alertToDelete.manufacturer} • {alertToDelete.quantity_available} units • ${alertToDelete.unit_price?.toFixed(2)}/unit
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmDelete} 
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Quote Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              Request Quote
            </DialogTitle>
            <DialogDescription>
              Send a quote request to the supplier for this product
            </DialogDescription>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-4">
              {/* Product Details (Read-only) */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900">{selectedAlert.product_name}</h4>
                    <p className="text-sm text-slate-600">
                      {selectedAlert.manufacturer} • MFR#: {selectedAlert.manufacturer_number || "N/A"}
                    </p>
                  </div>
                  {getAlertBadge(selectedAlert.alert_type)}
                </div>
                
                {selectedAlert.description && (
                  <p className="text-sm text-slate-600">{selectedAlert.description}</p>
                )}
                
                <div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500">Available</p>
                    <p className="font-semibold text-emerald-600">{selectedAlert.quantity_available} units</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Unit Price</p>
                    <p className="font-semibold text-slate-900">${selectedAlert.unit_price?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lead Time</p>
                    <p className="font-semibold text-slate-900">{selectedAlert.lead_time_days} days</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">From: <strong>{selectedAlert.supplier_name}</strong></span>
                  {selectedAlert.supplier_email && (
                    <span className="text-sm text-slate-400">({selectedAlert.supplier_email})</span>
                  )}
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quantity">Quantity Needed *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      max={selectedAlert.quantity_available}
                      value={quoteForm.quantity}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="Enter quantity"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Max available: {selectedAlert.quantity_available}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="urgency">Urgency</Label>
                    <select
                      id="urgency"
                      value={quoteForm.urgency}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, urgency: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                    >
                      <option value="low">Low - Within 2 weeks</option>
                      <option value="normal">Normal - Within 1 week</option>
                      <option value="high">High - Within 3 days</option>
                      <option value="critical">Critical - ASAP</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special requirements or questions for the supplier..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Estimated Total */}
                {quoteForm.quantity && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-700">Estimated Total</span>
                      <span className="text-lg font-bold text-blue-900">
                        ${(parseInt(quoteForm.quantity) * selectedAlert.unit_price).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      {quoteForm.quantity} units × ${selectedAlert.unit_price?.toFixed(2)}/unit
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestQuote} 
              disabled={submitting || !quoteForm.quantity}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Quote Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Export a compact bell icon with badge for header use
export function CampaignAlertsBell({ token, userType = "hospital", onClick }) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      if (!token) return;
      
      try {
        const endpoint = userType === "admin" 
          ? "/api/campaign-alerts/admin" 
          : "/api/campaign-alerts/hospital";
        
        const res = await fetch(`${API}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.unread_count || 0);
        }
      } catch (err) {
        console.error("Failed to fetch alert count:", err);
      }
    };
    
    fetchCount();
    // Poll every 30 seconds for live updates
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, userType]);

  return (
    <button
      onClick={onClick}
      className="relative p-2 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
      data-testid="campaign-alerts-bell"
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-amber-500 text-white rounded-full text-xs font-bold min-w-[18px] text-center">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}
